// Two-tier docs manifest. Eager metadata + lazy article bodies — keeps the
// initial JS bundle lean as the knowledge base scales (a 200-article KB
// would otherwise eagerly bundle every article body even for users who
// never open /docs).
//
// Sources:
//   - frontmatter + headings (eager): `virtual:docs-metadata`
//     (src/docs/mdx/docsMetadataPlugin.mts). The values are the article
//     modules' own `export const frontmatter` (`remark-mdx-frontmatter`) and
//     `export const headings` (`remarkExportHeadings`), copied out at load
//     time, so reading them no longer evaluates every article body. Two
//     eager globs did that until 2026-10-21 (plan
//     2026-10-21-ci-heavy-suites-less-work, step 8b).
//   - default export (lazy): the React component for the article body
//   - reading-times sidecar (eager, optional): JSON written by
//     `scripts/validate-docs.ts` at predev/pretest/prebuild time. The
//     directory is gitignored, so on a fresh clone the file is missing
//     until the first hook run — a missing import must DEGRADE GRACEFULLY
//     to "no reading-time anywhere" rather than crashing buildManifest().
//
// Headings extraction was previously stubbed (`headings: []`) because the
// `?raw` glob is intercepted by the MDX plugin's `enforce: "pre"` transform.
// The remark-plugin path bypasses that race entirely.

import type { ComponentType } from "react";
import type { DocMeta, DocSource } from "@/types/docs";
import {
  validateDocLocation,
  validateFrontmatter,
  validateGroupRegistry,
  validatePublicSummaries,
} from "./validation";
import { groupFolderOf } from "./docGroups";
import { validateRedirects } from "./docRedirects";
import { isHelpPath, normalizeSlug, pathToSlug } from "./normalizeSlug";
import { docsMetadata } from "virtual:docs-metadata";

// The negative `!…/_help/…` pattern (and the same exclusion in
// `virtual:docs-metadata`) keeps the drawer-only help topics out of the docs
// manifest (and therefore out of the sidebar, search, category pages, and
// route guard — all of which read this manifest). `buildManifest`
// additionally guards with `isHelpPath` as the authoritative single-source
// predicate, so the docs/help split can never drift even if a pattern is
// edited. Help topics are loaded separately via src/docs/helpManifest.ts.
const lazyModules = import.meta.glob(["/docs/**/*.mdx", "!/docs/**/_help/**/*.mdx"]) as Record<
  string,
  () => Promise<{ default: ComponentType }>
>;

// Reading-times sidecar — written by validate-docs.ts. Wrap in a
// `import.meta.glob` so a missing file resolves to an empty module
// dictionary rather than a hard build break. (Static `import` would
// fail on a fresh clone before the first predev/pretest run.)
const readingTimesModules = import.meta.glob<{ default: Record<string, number> }>(
  "./__generated__/reading-times.json",
  { eager: true }
);
const readingTimes: Record<string, number> = (() => {
  const entry = Object.values(readingTimesModules)[0];
  return entry?.default ?? {};
})();

function buildManifest(): DocMeta[] {
  const entries: DocMeta[] = [];
  const seenSlugs = new Set<string>();

  for (const [path, { frontmatter: fm, headings }] of Object.entries(docsMetadata)) {
    // Authoritative docs/help split — `virtual:docs-metadata` already drops
    // help topics, but this guard makes `isHelpPath` the single source of
    // truth (and keeps the manifest correct if that file set is ever
    // edited). Help topics live in the help manifest, never here.
    if (isHelpPath(path)) continue;

    const slug = pathToSlug(path);
    validateFrontmatter(slug, fm);
    validateDocLocation(slug, fm);

    if (seenSlugs.has(slug)) {
      throw new Error(
        `[docs] duplicate slug "${slug}" — multiple files normalize to the same URL. Check apps/frontend/docs/ for case collisions.`
      );
    }
    seenSlugs.add(slug);

    entries.push({
      ...fm,
      slug,
      section: slug.split("/")[0],
      group: groupFolderOf(slug),
      // Fall back to [] if remarkExportHeadings is misconfigured or the
      // export is missing for any reason — better to ship a search-text
      // gap than to crash buildManifest.
      headings: headings ?? [],
      readingTime: readingTimes[slug],
    });
  }

  validateGroupRegistry(seenSlugs);
  validateRedirects(seenSlugs);
  validatePublicSummaries(entries.map((e) => ({ slug: e.slug, fm: e })));

  if (entries.length === 0) {
    throw new Error(
      "[docs] manifest assembly produced zero entries — check that apps/frontend/docs/ contains .mdx files and that the import.meta.glob path matches."
    );
  }

  // Sort within section by `order` (numeric, asc), then alphabetically by title.
  // This is NOT the reading order: `order` is relative to an article's submenu
  // or group, so anything shown in sequence (sidebar, category page, prev/next)
  // goes through buildSidebarTree instead.
  entries.sort((a, b) => {
    if (a.section !== b.section) return a.section.localeCompare(b.section);
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.title.localeCompare(b.title);
  });

  return entries;
}

export const manifest: DocMeta[] = buildManifest();

const slugToPath = new Map<string, string>(
  Object.keys(lazyModules)
    .filter((p) => !isHelpPath(p))
    .map((p) => [pathToSlug(p), p])
);

export const docSource: DocSource = {
  list: () => manifest,
  load: async (slug) => {
    const normalized = normalizeSlug(slug);
    const path = slugToPath.get(normalized);
    if (!path) throw new Error(`[docs] unknown slug: ${slug}`);
    const loader = lazyModules[path];
    return loader() as Promise<{ default: ComponentType }>;
  },
};

/** Section-display labels for sidebar/breadcrumbs/category pages. Module
 * sections come from MENU_REGISTRY; universal sections need their own labels. */
// Defined in validation.ts (no Vite globs) so build scripts can read it too.
export { UNIVERSAL_SECTION_LABELS } from "./validation";
